import { DialogService } from './core/dialog/dialog.service';
import { ConnectionStateService } from './shared/services/connection-state.service';
import { trackAppHistory } from "./shared/screen-shell/screen-navigation.utils";
import { Component, Injector, OnInit } from "@angular/core";
import { BehaviorSubject, combineLatest, EMPTY, Observable, of, Subscription, throwError } from "rxjs";
import { environment } from "../app/environments/environment";
import { nativeAppUrl, androidAppDownloadUrl, iosAppDownloadUrl } from "./shared/utils/native-app-url.utils";
import { Router, NavigationEnd } from "@angular/router";
import { AuthService } from "@auth0/auth0-angular";
import { FootballerAuthService, usesFootballerAuth } from "./shared/services/footballer-auth.service";
import { HttpClient } from "@angular/common/http";
import { Player } from "./shared/models/player";
import { catchError, filter, map, switchMap, take } from "rxjs/operators";
import { ApiVersion } from "./shared/models/apiVersion";
import { PlayerService } from "./shared/services/player.service";
import { LocaleService } from "./shared/i18n/locale.service";
import { TranslateService } from "@ngx-translate/core";
import { AnalyticsService } from "./shared/services/analytics.service";
import { AuthSessionRecoveryService } from "./shared/services/auth-session-recovery.service";
import { AuthRedirectService } from "./shared/services/auth-redirect.service";
import { GuestUpgradeClaimService, GuestClaimRecovery } from "./shared/services/guest-upgrade-claim.service";
import { SupportedLanguage } from "./shared/models/player";
import { AuthenticatedSessionStateService } from "./shared/services/authenticated-session-state.service";
import { GuestOwnerSessionService } from "./shared/services/guest-owner-session.service";
import { GuestOwnerService } from "./shared/services/guest-owner.service";
import { LoginIdentityMigrationService } from "./shared/services/login-identity-migration.service";
import { detectMobilePlatform } from "./shared/utils/mobile-platform.utils";
import { LocalDevelopmentResetService } from "./shared/services/local-development-reset.service";

declare const gtag: any;

interface AuthenticatedProfileBootstrap {
  player: Player;
  profileCreated: boolean;
}

@Component({
  selector: "app-root",
  providers: [],
  templateUrl: "app.component.html",
  styleUrls: ["./app.component.css"],
  standalone: false,
})
export class AppComponent implements OnInit {
  public readonly androidAppUrl = androidAppDownloadUrl;
  public readonly iosAppUrl = iosAppDownloadUrl;
  public readonly mobileAppUrl = nativeAppUrl("home");
  public readonly mobilePlatform = detectMobilePlatform();
  public readonly showAndroidAppLink = this.mobilePlatform === null || this.mobilePlatform === "android";
  public readonly showIosAppLink = this.mobilePlatform === null || this.mobilePlatform === "ios";
  public readonly bootstrapReady$ = new BehaviorSubject<boolean>(false);
  public readonly authRedirectInProgress$ = new BehaviorSubject<boolean>(false);
  public currentPlayer: Player;
  public bootstrapErrorKey: string;
  public authMethodMismatch: { existingAuth: string; attemptedWith: string };
  public guestLandingError: string;
  public guestUpgradeClaimInProgress = false;
  public guestUpgradeClaimErrorMsg: string;
  public guestUpgradeClaimCompleted = false;
  public guestClaimRecovery: GuestClaimRecovery;
  public guestClaimDeferred = false;
  public showLandingLanguageSelection = false;
  public selectedLandingLanguage: SupportedLanguage = "en";
  public guestLandingTeamName = "";
  public readonly showDevBanner: boolean = !environment.production;
  public readonly canResetLocalDev: boolean = !environment.production && environment.API_BASE_URL.indexOf("nogosh.local") >= 0;
  public readonly devBannerLabel: string = environment.API_BASE_URL.indexOf("nogosh.local") >= 0 ? "LOCAL DEV"
    : environment.API_BASE_URL.indexOf("app.staging.footballer.zone") >= 0 ? "STAGING" : "DEV";
  public authIsAuthenticated$: Observable<boolean> = of(false);
  public authIsLoading$: Observable<boolean> = of(true);
  subscription: Subscription;
  private guestUpgradeClaimAttempted = false;
  private publicGuestNavigationInProgress = false;
  private authRedirectReleaseTimeout: number;
  private auth: AuthService;

  constructor(
    private injector: Injector,
    private router: Router,
    private authHttp: HttpClient,
    private playerService: PlayerService,
    private localeService: LocaleService,
    private translate: TranslateService,
    private analytics: AnalyticsService,
    private authSessionRecovery: AuthSessionRecoveryService,
    private authenticatedSessionState: AuthenticatedSessionStateService,
    private guestOwnerSessionService: GuestOwnerSessionService,
    private guestOwnerService: GuestOwnerService,
    private loginIdentityMigrationService: LoginIdentityMigrationService,
    private localDevelopmentResetService: LocalDevelopmentResetService,
    private dialogService: DialogService,
    public connection: ConnectionStateService,
  ) {
    if (!this.isPublicInviteRoute()) {
      this.analytics.initializeRouteTracking(this.router);
    }

    trackAppHistory(this.router);
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.publicGuestNavigationInProgress = false;
        this.disablePointerHoverUntilMouseMove();
        this.resetDocumentScroll();
        this.refreshGuestLandingTeamName();
      }
    });

    if (environment.production) {
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          gtag("event", "page", event.urlAfterRedirects);
        }
      });
    }
  }

  ngOnInit() {
    this.initializePointerHoverGuard();
    this.redirectPublicGuestHashRoute();
    this.initializePublicLandingLanguage();
    this.refreshGuestLandingTeamName();

    if (this.isPublicGuestRoute() && !this.isPublicProfileRoute()) {
      this.bootstrapReady$.next(true);
      if (this.isPublicInviteRoute() || usesFootballerAuth) {
        this.initializeAuth();
        this.initializeAuthTransitionGuard();
        this.auth.error$.subscribe((error) => {
          this.clearAuthRedirectState();
          if (!this.authSessionRecovery.recoverFromStaleSession(error)) {
            console.error("Auth0 error", error);
          }
        });
        this.initializeAuthenticatedProfile();
      } else {
        this.authIsLoading$ = of(false);
      }
      return;
    }

    this.initializeAuth();
    this.initializeAuthTransitionGuard();
    this.auth.error$.subscribe((error) => {
      this.clearAuthRedirectState();
      if (!this.authSessionRecovery.recoverFromStaleSession(error)) {
        console.error("Auth0 error", error);
      }
    });
    this.initializeAuthenticatedProfile();

    this.auth.isAuthenticated$.subscribe((sub) => console.log("IS logged in", sub));
  }

  public currentPlayerBadgeLabel(): string {
    if (!this.currentPlayer) {
      return "";
    }

    return (this.currentPlayer.nickName || this.currentPlayer.name || this.currentPlayer.email || "").trim();
  }

  public async resetLocalDevelopmentBrowser(): Promise<void> {
    if (!this.canResetLocalDev) return;
    this.dialogService.alert({
      title: this.translate.instant("localDevelopment.resetTitle"),
      text: this.translate.instant("localDevelopment.webResetMessage"),
      closeLabel: this.translate.instant("common.cancel"),
      actionLabel: this.translate.instant("localDevelopment.resetAction"),
      destructive: true,
      action: () => { void this.performLocalDevelopmentReset(); },
    });
  }

  private async performLocalDevelopmentReset(): Promise<void> {
    await this.localDevelopmentResetService.clearBrowserData();
    const auth = this.auth || this.injector.get(AuthService);
    auth.logout({
      logoutParams: {
        returnTo: window.location.origin + "/",
      },
    } as any);
  }

  private initializePointerHoverGuard(): void {
    window.addEventListener("mousemove", () => {
      document.body.classList.add("pointer-hover-enabled");
    });
  }

  private disablePointerHoverUntilMouseMove(): void {
    document.body.classList.remove("pointer-hover-enabled");
  }

  private resetDocumentScroll(): void {
    window.setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;

      window.requestAnimationFrame(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      });
    }, 0);
  }

  login(): void {
    console.log("Auth0 login requested", window.location.origin);
    this.injector
      .get(AuthRedirectService)
      .login(this.router.url)
      .subscribe(
        () => console.log("Auth0 login redirect started"),
        (error) => console.error("Auth0 login redirect failed", error),
      );
  }

  signup(): void {
    this.injector
      .get(AuthRedirectService)
      .signup(this.router.url)
      .subscribe(
        () => undefined,
        (error) => console.error("Auth0 signup redirect failed", error),
      );
  }

  private initializeAuth(): void {
    this.auth = this.injector.get(AuthService);
    this.authIsAuthenticated$ = this.auth.isAuthenticated$;
    this.authIsLoading$ = this.auth.isLoading$;
  }

  private initializeAuthTransitionGuard(): void {
    if (usesFootballerAuth) {
      this.clearAuthRedirectState();
      return;
    }
    if (this.hasAuthRedirectMarker() || this.hasAuthCallbackParams()) {
      this.authRedirectInProgress$.next(true);
    }

    combineLatest([this.auth.isLoading$, this.auth.isAuthenticated$]).subscribe(([loading, authenticated]) => {
      if (authenticated) {
        this.clearAuthRedirectState();
        return;
      }

      if (loading || this.hasAuthCallbackParams()) {
        return;
      }

      if (this.authRedirectInProgress$.value) {
        this.scheduleAuthRedirectRelease();
      }
    });
  }

  private scheduleAuthRedirectRelease(): void {
    if (this.authRedirectReleaseTimeout) {
      return;
    }

    this.authRedirectReleaseTimeout = window.setTimeout(() => {
      this.clearAuthRedirectState();
    }, 1500);
  }

  private hasAuthCallbackParams(): boolean {
    const params = new URLSearchParams(window.location.search || "");
    return params.has("code") || params.has("state") || params.has("error");
  }

  private hasAuthRedirectMarker(): boolean {
    try {
      return !!sessionStorage.getItem(AuthRedirectService.redirectStartedKey);
    } catch (_) {
      return false;
    }
  }

  private clearAuthRedirectState(): void {
    if (this.authRedirectReleaseTimeout) {
      window.clearTimeout(this.authRedirectReleaseTimeout);
      this.authRedirectReleaseTimeout = null;
    }

    try {
      sessionStorage.removeItem(AuthRedirectService.redirectStartedKey);
    } catch (_) {
    }

    this.authRedirectInProgress$.next(false);
  }

  private initializeAuthenticatedProfile(): void {
    this.auth.user$
      .pipe(
        filter((profile) => profile != null),
        switchMap((profile) => {
          this.bootstrapErrorKey = null;
          this.clearCachedStateForDifferentAuthenticatedUser(profile.sub);
          // if (navigator.geolocation) {
          //   navigator.geolocation.getCurrentPosition((position) => {
          //     profile["location"] = {
          //       latitude: position.coords.latitude,
          //       longitude: position.coords.longitude,
          //     };
          //   });
          // } else {
          //   console.log("NE PODRZAVA");
          // }

          // /*   profile["location"] = {
          //   latitude: "45.7471683",
          //   longitude: "15.7944213",
          // }; */
          profile.user_id = profile.sub;
          return this.loginIdentityMigrationService.completePendingMigration().pipe(
            switchMap(() => this.authHttp.post<Player>(environment.API_BASE_URL + "/profile", profile)),
            switchMap((savedPlayer) =>
              this.playerService.getCurrentUserProfile().pipe(
                map((player) => ({
                  player,
                  profileCreated: !!(savedPlayer && savedPlayer.profileCreated),
                })),
              ),
            ),
            switchMap((bootstrap: AuthenticatedProfileBootstrap) =>
              this.applyPublicLanguageSelection(bootstrap.player).pipe(
                map((player) => ({ player, profileCreated: bootstrap.profileCreated })),
              ),
            ),
            switchMap((bootstrap: AuthenticatedProfileBootstrap) => this.localeService.initialize(bootstrap.player).pipe(map(() => bootstrap))),
            catchError((error) => {
              if (!usesFootballerAuth) return throwError(() => error);
              // A failed profile fetch must not permanently unsubscribe subsequent login attempts.
              this.bootstrapErrorKey = "errors.profileLoad";
              this.bootstrapReady$.next(true);
              return EMPTY;
            }),
          );
        }),
      )
      .subscribe(
        (bootstrap: AuthenticatedProfileBootstrap) => {
          const response = bootstrap.player;
          this.currentPlayer = this.normalizeCurrentPlayer(response);
          this.guestUpgradeClaimAttempted = false;
          this.guestClaimRecovery = null;
          this.setCurrentPlayer(this.currentPlayer);
          if (usesFootballerAuth) {
            this.injector.get(FootballerAuthService).profileReady$.next(this.currentPlayer.authId);
          }
          this.runPendingGuestClaims();
          this.bootstrapReady$.next(true);
          this.analytics.trackSessionStarted();
          this.analytics.trackLoginSuccess();
          if (bootstrap.profileCreated && !this.isClaimRoute() && !this.isPublicGuestRoute()) {
            this.router.navigate(["/player-detail", false]);
          }
        },
        (error) => {
          if (this.isAuthMethodMismatch(error)) {
            this.authMethodMismatch = {
              existingAuth: error.error.existingAuth,
              attemptedWith: error.error.attemptedWith,
            };
            this.bootstrapErrorKey = null;
          } else {
            this.bootstrapErrorKey = "errors.profileLoad";
          }
          this.bootstrapReady$.next(true);
        },
      );
  }

  public createGuestMatch(): void {
    this.guestLandingError = null;
    this.publicGuestNavigationInProgress = true;
    const existingSession = this.guestOwnerSessionService.getSession();
    if (existingSession && existingSession.guestOwnerToken) {
      this.router
        .navigate(["/guest-manager"])
        .catch(() => {
          this.publicGuestNavigationInProgress = false;
        });
      return;
    }

    this.router
      .navigate(["/guest-manager"], {
        queryParams: {
          autoCreate: "true",
          draftName: this.randomGuestTeamName(),
        },
      })
      .catch(() => {
        this.publicGuestNavigationInProgress = false;
      });
  }

  public guestLandingPrimaryLabel(): string {
    return this.guestLandingTeamName || this.translate.instant("landing.createAsGuest");
  }

  public selectLandingLanguage(language: SupportedLanguage): void {
    this.selectedLandingLanguage = language;
    this.localeService.usePublic(language).subscribe(
      () => (this.showLandingLanguageSelection = false),
      () => (this.showLandingLanguageSelection = false),
    );
  }

  public showLandingLanguageChooser(): void {
    this.showLandingLanguageSelection = true;
    this.guestLandingError = null;
  }

  public isSelectedLandingLanguage(language: SupportedLanguage): boolean {
    return this.selectedLandingLanguage === language;
  }

  public isClaimRoute(): boolean {
    return this.isClaimPath(this.router.url) || this.isClaimPath(window.location.pathname) || this.isClaimPath(this.currentHashPath());
  }

  public isPublicGuestRoute(): boolean {
    return (
      this.publicGuestNavigationInProgress ||
      this.isPublicGuestPath(this.router.url) ||
      this.isPublicGuestPath(window.location.pathname) ||
      this.isPublicGuestPath(this.currentHashPath())
    );
  }

  public isAppShellRoute(): boolean {
    return !this.isPublicGuestRoute() || this.isPublicProfileRoute();
  }

  public isPublicProfileRoute(): boolean {
    return (
      this.isPublicProfilePath(this.router.url) ||
      this.isPublicProfilePath(window.location.pathname) ||
      this.isPublicProfilePath(this.currentHashPath())
    );
  }

  public isPublicInviteRoute(): boolean {
    return this.isPublicInvitePath(this.router.url) || this.isPublicInvitePath(window.location.pathname) || this.isPublicInvitePath(this.currentHashPath());
  }

  public isPublicRouteActivated(): boolean {
    // The browser URL can change before Angular activates its destination.
    // Do not mount the previous (authenticated) root screen in a public outlet.
    return this.isPublicGuestPath(this.router.url);
  }

  private redirectPublicGuestHashRoute(): void {
    const hashPath = this.currentHashPath();

    if (
      hashPath.indexOf("/claim/") === 0 ||
      hashPath.indexOf("/guest-upgrade") === 0 ||
      hashPath.indexOf("/guest-manager") === 0 ||
      hashPath.indexOf("/join/") === 0 ||
      hashPath.indexOf("/public-event-invite/") === 0 ||
      hashPath.indexOf("/profile/") === 0 ||
      hashPath.indexOf("/i/") === 0 ||
      hashPath.indexOf("/email-verified") === 0
    ) {
      this.router.navigateByUrl(hashPath);
    }
  }

  private currentHashPath(): string {
    return window.location.hash && window.location.hash.indexOf("#/") === 0 ? window.location.hash.substring(1) : "";
  }

  private isPublicInvitePath(path: string): boolean {
    return path && (path.indexOf("/join/") === 0 || path.indexOf("/public-event-invite/") === 0 || path.indexOf("/i/") === 0);
  }

  private isClaimPath(path: string): boolean {
    return path && path.indexOf("/claim/") === 0;
  }

  private isPublicProfilePath(path: string): boolean {
    return path && path.indexOf("/profile/") === 0;
  }

  private isPublicGuestPath(path: string): boolean {
    if (path && (path === '/auth' || path.startsWith('/auth?') || path.startsWith('/auth#'))) return true;
    return path && (this.isClaimPath(path) || path.indexOf("/guest-manager") === 0 || path.indexOf("/guest-upgrade") === 0 || path.indexOf("/email-verified") === 0 || this.isPublicProfilePath(path) || this.isPublicInvitePath(path));
  }

  private setCurrentPlayer(player: Player): void {
    // problems with circular dependency if this function is used from playerService

    PlayerService.normalizePlayerAvatar(player);

    this.playerService.setCurrentPlayer(player);
  }

  public onLanguageSelectionCompleted(player: Player): void {
    this.currentPlayer = this.normalizeCurrentPlayer(player);
    this.setCurrentPlayer(this.currentPlayer);
  }

  public retryBootstrap(): void {
    window.location.reload();
  }

  public restartLoginAfterMethodMismatch(): void {
    this.clearAuthenticatedLocalState();
    this.auth.logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    } as any);
  }

  public continueAsGuestAfterMethodMismatch(): void {
    this.clearAuthenticatedLocalState();
    this.auth.logout({
      logoutParams: {
        returnTo: window.location.origin + "/",
      },
    } as any);
  }

  public retryGuestUpgradeClaim(): void {
    this.guestUpgradeClaimAttempted = false;
    this.runPendingGuestClaims(true);
  }

  public deferGuestClaim(): void {
    this.injector.get(GuestUpgradeClaimService).saveRecoveryDecision(this.guestClaimRecovery.key, "later");
    this.guestClaimDeferred = true;
  }

  public forgetGuestClaim(): void {
    const recovery = this.guestClaimRecovery;
    this.dialogService.alert({
      title: this.translate.instant("guestUpgrade.recovery.forget"),
      text: this.translate.instant("guestUpgrade.recovery.forgetHint"),
      closeLabel: this.translate.instant("common.cancel"),
      actionLabel: this.translate.instant("guestUpgrade.recovery.forget"),
      action: () => {
        this.injector.get(GuestUpgradeClaimService).saveRecoveryDecision(recovery.key, "forgotten");
        this.guestClaimRecovery = null;
        this.guestUpgradeClaimErrorMsg = null;
      }
    });
  }

  public confirmGuestClaim(): void {
    if (!this.guestClaimRecovery?.canClaim || this.guestUpgradeClaimInProgress) return;
    const service = this.injector.get(GuestUpgradeClaimService);
    this.guestUpgradeClaimInProgress = true;
    this.guestUpgradeClaimErrorMsg = null;
    service.claimPendingGuestState().subscribe({
      next: (result) => {
        this.guestUpgradeClaimInProgress = false;
        if (result.success) {
          this.guestClaimRecovery = null;
          this.guestUpgradeClaimCompleted = true;
        } else {
          const terminal = [403, 404, 409].includes(result.error?.status);
          this.guestClaimRecovery = { ...this.guestClaimRecovery,
            canClaim: false, reason: terminal ? "unavailable" : "connection" };
        }
      },
      error: () => {
        this.guestUpgradeClaimInProgress = false;
        this.guestClaimRecovery = { ...this.guestClaimRecovery, canClaim: false, reason: "connection" };
      }
    });
  }

  private normalizeCurrentPlayer(player: Player): Player {
    player.preferredLanguage = this.localeService.normalize(player.preferredLanguage);
    return player;
  }

  private applyPublicLanguageSelection(player: Player): Observable<Player> {
    const publicLanguage = this.localeService.cachedPublicLanguage();
    if (!publicLanguage || !player || player.languageSelectionCompleted !== false) {
      return of(player);
    }

    return this.playerService.updatePreferredLanguage(publicLanguage).pipe(
      map((updatedPlayer) => {
        updatedPlayer.preferredLanguage = publicLanguage;
        updatedPlayer.languageSelectionCompleted = true;
        return updatedPlayer;
      }),
      catchError((error) => {
        console.warn("Unable to apply cached public language to the current profile.", error);
        return of(player);
      }),
    );
  }

  private isAuthMethodMismatch(error: any): boolean {
    return !!(error && error.error && error.error.existingAuth && error.error.attemptedWith);
  }

  private clearAuthenticatedLocalState(): void {
    this.authenticatedSessionState.clear();
    this.playerService.clearCurrentPlayer();
    this.currentPlayer = null;
    this.authMethodMismatch = null;
    this.bootstrapErrorKey = null;
  }

  private clearCachedStateForDifferentAuthenticatedUser(authId: string): void {
    const cachedPlayer = this.playerService.peekCurrentPlayer();
    if (!cachedPlayer || !authId || cachedPlayer.authId === authId) {
      return;
    }

    this.authenticatedSessionState.clearAppState();
    this.playerService.clearCurrentPlayer();
    this.currentPlayer = null;
  }

  private initializePublicLandingLanguage(): void {
    const cachedLanguage = this.localeService.cachedPublicLanguage();
    if (cachedLanguage) {
      this.selectedLandingLanguage = cachedLanguage;
      this.localeService.usePublic(cachedLanguage).subscribe(
        () => {},
        () => {},
      );
      return;
    }

    this.selectedLandingLanguage = this.defaultPublicLanguage();
    this.showLandingLanguageSelection = true;
  }

  private refreshGuestLandingTeamName(): void {
    const session = this.guestOwnerSessionService.getSession();
    this.guestLandingTeamName = session && session.eventName ? session.eventName.trim() : "";

    if (!session || !session.guestOwnerToken || this.isPublicGuestRoute()) {
      return;
    }

    this.guestOwnerService.currentEvent().pipe(take(1)).subscribe(
      (response) => {
        if (!response || !response.event) {
          this.guestOwnerSessionService.clearCurrentEvent();
          this.guestLandingTeamName = "";
          return;
        }

        this.guestLandingTeamName = (response.event.name || "").trim();
        this.guestOwnerSessionService.saveCurrentEvent(response.event.id, this.guestLandingTeamName);
      },
      (error) => {
        const status = error && error.status;
        if (status === 401 || status === 403 || status === 404 || status === 409) {
          this.guestOwnerSessionService.clearSession();
          this.guestLandingTeamName = "";
        }
      },
    );
  }

  private defaultPublicLanguage(): SupportedLanguage {
    const browserLanguages = typeof navigator !== "undefined" && navigator.languages ? navigator.languages : [];

    return browserLanguages.some((language) => language && language.toLowerCase().indexOf("hr") === 0) ? "hr" : "en";
  }

  private randomGuestTeamName(): string {
    const names =
      this.localeService.currentLanguage === "hr"
        ? [
            "Krivi Teren",
            "Kasni Start",
            "Bez Zagrijavanja",
            "Treće Poluvrijeme",
            "Spori Kontranapad",
            "Lažna Devetka",
            "Samo Dodaj",
            "Autogol United",
            "Nema Zamjene",
            "Zadnji Poziv",
            "Puni Sprint",
            "Mali Nogomet Veliki Planovi",
            "Kopačke u Magli",
            "VAR Ne Radi",
            "Daj Još Jednu",
            "Ekipa iz Kvarta",
            "Nismo Se Dogovorili",
            "Prva Stativa",
            "Bez Faula",
            "Igramo Do Mraka",
          ]
        : [
            "Wrong Pitch",
            "Late Kickoff",
            "No Warmup",
            "Third Half FC",
            "Slow Counterattack",
            "False Nine",
            "Just Pass It",
            "Own Goal United",
            "No Subs Left",
            "Last Call FC",
            "Full Sprint",
            "Small Goals Big Plans",
            "Boots in the Fog",
            "VAR Is Down",
            "One More Game",
            "Neighborhood XI",
            "We Never Agreed",
            "Near Post FC",
            "No Foul",
            "Play Until Dark",
          ];

    return names[Math.floor(Math.random() * names.length)];
  }

  private runPendingGuestClaims(expand: boolean = false): void {
    if (this.router.url.indexOf("/guest-upgrade") === 0) {
      return;
    }
    const guestUpgradeClaimService = this.injector.get(GuestUpgradeClaimService);
    if (!guestUpgradeClaimService.hasPendingClaim()) {
      this.guestClaimRecovery = null;
      return;
    }
    if (this.guestUpgradeClaimAttempted) {
      return;
    }

    this.guestUpgradeClaimAttempted = true;
    this.guestUpgradeClaimErrorMsg = null;
    const playerId = this.currentPlayer.id;
    guestUpgradeClaimService.inspectPendingClaim(playerId).subscribe(
      (result) => {
        if (this.currentPlayer?.id !== playerId) return;
        const decision = result && guestUpgradeClaimService.recoveryDecision(result.key);
        this.guestClaimRecovery = decision === "forgotten" ? null : result;
        this.guestClaimDeferred = decision === "later" && !expand;
      },
      () => {
        this.guestUpgradeClaimInProgress = false;
        this.guestUpgradeClaimErrorMsg = this.translate.instant("guestUpgrade.errors.moveFailed");
      },
    );
  }

  private showFooter(): boolean {
    return !this.router.url.includes("messages");
    //console.log("current url: " + this.router.url);
  }
}
